import type { ReactNode, MouseEvent } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface DataCardProps {
  selected?: boolean;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  children: ReactNode;
}

/**
 * Opinionated card shell for responsive table → card layouts.
 * Mirrors the look of `LateOrderCard` so all card-mode tables match.
 * Use the `Header`, `Chips`, and `Body` subcomponents to slot content.
 */
export function DataCard({ selected, onClick, className, children }: DataCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full text-left rounded-lg border bg-card p-4 transition-colors hover:bg-accent",
        "flex flex-col gap-3",
        selected && "ring-2 ring-primary",
        className,
      )}
    >
      {children}
    </button>
  );
}

function DataCardHeader({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("flex items-center gap-2", className)}>{children}</div>;
}

function DataCardChips({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>{children}</div>
  );
}

function DataCardBody({
  children,
  columns = 2,
  className,
}: {
  children: ReactNode;
  columns?: 1 | 2;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid gap-4",
        columns === 2 ? "grid-cols-2" : "grid-cols-1",
        className,
      )}
    >
      {children}
    </div>
  );
}

interface DataCardFieldProps {
  icon?: LucideIcon;
  label?: string;
  tone?: "default" | "warning";
  children: ReactNode;
  /** Optional secondary line (timestamps, scheduled vs actual, etc.). */
  secondary?: ReactNode;
  className?: string;
}

/** A single label/value pair used inside `DataCard.Body`. */
function DataCardField({
  icon: Icon,
  label,
  tone = "default",
  children,
  secondary,
  className,
}: DataCardFieldProps) {
  const toneClass =
    tone === "warning" ? "text-[hsl(var(--aurora-status-warning))]" : "text-muted-foreground";
  return (
    <div className={cn("min-w-0", className)}>
      <div className="flex items-center gap-1.5 text-sm font-medium">
        {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
        <span className="truncate">{children}</span>
      </div>
      {label ? (
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground/80 mt-0.5">
          {label}
        </div>
      ) : null}
      {secondary ? (
        <div className={cn("text-xs mt-0.5", toneClass)}>{secondary}</div>
      ) : null}
    </div>
  );
}

DataCard.Header = DataCardHeader;
DataCard.Chips = DataCardChips;
DataCard.Body = DataCardBody;
DataCard.Field = DataCardField;

/**
 * Detail-panel style row (label left, value right) used by the responsive
 * list cards. Wrap a group in `divide-y divide-border`.
 */
function DataCardDetailRow({
  label,
  value,
  className,
}: {
  label: string;
  value?: ReactNode;
  className?: string;
}) {
  const hasValue = value !== undefined && value !== null && value !== "" && value !== false;
  return (
    <div className={cn("flex justify-between items-start py-2 gap-2", className)}>
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <div className="text-sm font-medium text-right max-w-[65%] min-w-0 flex justify-end">
        {hasValue ? value : "—"}
      </div>
    </div>
  );
}

DataCard.DetailRow = DataCardDetailRow;
export { DataCardDetailRow as DetailRow };

interface DataCardTileProps {
  icon?: LucideIcon;
  label: string;
  /** Primary value. When omitted, the label renders as a muted placeholder. */
  value?: ReactNode;
  /** Optional secondary line under the value (e.g. trailer type / status). */
  subtext?: ReactNode;
  /** Optional trailing slot pinned to the right edge (icons, badges, actions). */
  trailing?: ReactNode;
  className?: string;
}

/**
 * Full-width labeled row used inside the responsive Order card.
 * Renders a muted rectangle with leading icon, label/value, optional
 * subtext, and an optional trailing slot.
 */
function DataCardTile({
  icon: Icon,
  label,
  value,
  subtext,
  trailing,
  className,
}: DataCardTileProps) {
  const hasValue = value !== undefined && value !== null && value !== "";
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-md bg-muted/40 px-3 py-2",
        className,
      )}
    >
      {Icon ? (
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      ) : null}
      <div className="min-w-0 flex-1">
        {hasValue ? (
          <>
            <div className="text-sm font-medium text-foreground truncate">
              {value}
            </div>
            {subtext ? (
              <div className="text-xs text-muted-foreground truncate">
                {subtext}
              </div>
            ) : null}
          </>
        ) : (
          <div className="text-sm text-muted-foreground truncate">{label}</div>
        )}
      </div>
      {trailing ? (
        <div className="shrink-0 inline-flex items-center gap-1">
          {trailing}
        </div>
      ) : null}
    </div>
  );
}

DataCard.Tile = DataCardTile;